/**
 * @fileoverview Processes and applies proposed code changes from a language model's output. Handles parsing model-generated change descriptions, identifies where changes should be applied in existing files, and supports creating new files or editing existing ones with context-aware replacement strategies.
 */

import { readFileSync, writeFileSync, existsSync } from 'fs'
import { applyEditRewrites, Rewrite, RewriteType, RewriteWithPath } from './rewrite'
import { error_block, info } from '@iterator/shared-node/util/logging'

// Unprocessed model output.
export type ProposedChange = {
  // Absolute path to the file to change. Does not need to exist.
  path: string

  // Model output for the proposed change. Needs to be parsed to apply to a file.
  text: string
}

// A processed version of ProposedChange.text. A proposed change may have multiple segments.
type ChangeSegment = {
  // The lines of the proposed change.
  lines: string[]

  // True if there is code before this segment. False if this is the first line of the file.
  priorExistingCode: boolean

  // True if there is code after this segment. False if this is the last line of the file.
  postExistingCode: boolean
}

/**
 * The format of a proposed change:
 *
 * 1. Starts with a code block delimiter (```) followed by the file path
 * 2. Contains the proposed changes, including:
 *    - Existing code lines for context to identify where to place the change
 *    - New or modified lines
 *    - "// ... existing code ..." to indicate skipped unchanged code
 * 3. Ends with a closing code block delimiter (```)
 *
 * Example:
 * ```/path/to/file.ts
 * // ... existing code ...
 * function existingFunction() {
 *   // Existing implementation
 * }
 *
 * // New function
 * function newFunction() {
 *   // New implementation
 * }
 *
 * // ... existing code ...
 * ```
 */
export function parseProposedChangesFromString(modelOutput: string): ProposedChange[] {
  const changes: ProposedChange[] = []
  let currentChange: ProposedChange | null = null
  const lines = modelOutput.split('\n')

  for (const line of lines) {
    if (line.startsWith('```') && line.trim().length > 3) {
      if (currentChange) {
        changes.push(currentChange)
      }
      currentChange = {
        path: line.trim().slice(3),
        text: '',
      }
    } else if (line.startsWith('```') && line.trim().length === 3 && currentChange) {
      changes.push(currentChange)
      currentChange = null
    } else if (currentChange) {
      currentChange.text += line + '\n'
    }
  }

  if (currentChange) {
    changes.push(currentChange)
  }

  return changes
}

/**
 * Updates files with the proposed changes. If a file does not exist, it is created.
 */
export function updateFilesFromProposedChanges(changes: ProposedChange[]): {
  rewrites: RewriteWithPath[]
  failedChanges: ProposedChange[]
} {
  const rewrites: RewriteWithPath[] = []
  const failedChanges: ProposedChange[] = []
  for (const change of changes) {
    if (existsSync(change.path)) {
      const fileContents = readFileSync(change.path, 'utf8')
      const {
        result,
        rewrites: changeRewrites,
        failedSegments,
      } = applyProposedChange(change, fileContents)
      writeFileSync(change.path, result)
      rewrites.push(
        ...changeRewrites.map((rewrite) => ({
          filePath: change.path,
          type: 'editFile' as RewriteType,
          ...rewrite,
        })),
      )
      failedChanges.push(...failedSegments.map((segment) => getFailedChange(change, segment)))
    } else {
      const changeText = change.text.trim()
      writeFileSync(change.path, changeText)
      const changeLines = changeText.split('\n')
      rewrites.push({
        filePath: change.path,
        type: 'createFile' as RewriteType,
        originalLineRange: { start: 0, end: 0 },
        originalLines: [],
        newLineRange: { start: 0, end: changeLines.length },
        newLines: changeLines,
      })
    }
  }
  return { rewrites, failedChanges }
}

function getFailedChange(change: ProposedChange, failedSegment: ChangeSegment): ProposedChange {
  let text = failedSegment.lines.join('\n')
  if (failedSegment.priorExistingCode) {
    text = '// ... existing code ...\n' + text
  }
  if (failedSegment.postExistingCode) {
    text += '\n// ... existing code ...'
  }
  return { path: change.path, text }
}
/**
 * Algorithm for matching and applying proposed changes:
 *
 * 1. Split the original file and proposed changes into lines.
 * 2. Iterate through segments of the proposed changes:
 *    a. If a segment starts with existing code, find the best matching position in the original file.
 *    b. Apply the changes line by line:
 *       - If lines match, keep the original line and move both pointers.
 *       - If lines differ and we're in a matching state, add the new line from changes.
 *       - If lines differ and we're not in a matching state, skip the original line to remove it.
 * 3. After applying all segments, append any remaining original lines if the last segment
 *    indicates there's existing code after it.
 *
 * We expect the LLM to provide multiple (hopefully 5) lines of existing code before and after
 * the change so that we can match and there won't be accidental collisions that lead to inserting
 * in the wrong place.
 *
 * @param change - The proposed change to apply.
 * @param original - The original file contents.
 * @returns The updated file contents, rewrite and segments that failed to apply.
 */
export function applyProposedChange(
  change: ProposedChange,
  original: string,
): { result: string; rewrites: Rewrite[]; failedSegments: ChangeSegment[] } {
  let lines = original.split('\n')

  const changeSegments = getChangeSegments(change.text.trim())
  const rewrites: Rewrite[] = []
  const failedSegments: ChangeSegment[] = []

  for (const changeSegment of changeSegments) {
    const { rewrite, status } = getRewriteForProposedChangeSegment(lines, changeSegment)
    if (rewrite && status === 'success') {
      lines = applyEditRewrites(lines, [rewrite], 'strict')
      rewrites.push(rewrite)
    } else if (status === 'error') {
      failedSegments.push(changeSegment)
    }
  }

  return { result: lines.join('\n'), rewrites, failedSegments }
}

/**
 * Applies a segment of proposed changes to the original file.
 *
 * @returns A Rewrite if the change results in different lines, undefined if the change is a no-op.
 */
export function getRewriteForProposedChangeSegment(
  originalLines: string[],
  changeSegment: ChangeSegment,
): { rewrite?: Rewrite; status: 'no-op' | 'error' | 'success' } {
  let matchStart = 0
  let matchCount = 0
  let matchEnd = originalLines.length

  if (changeSegment.priorExistingCode) {
    // Starting with "... existing code ...". Here we fast-forwards past the existing code.
    const { index, count } = findBestForwardMatchIndex(originalLines, changeSegment.lines)
    matchStart = index
    matchCount = count
  }

  if (changeSegment.postExistingCode) {
    // Ending with "... existing code ...". Here we fast-forwards past the existing code.
    matchEnd =
      matchStart + findIndexOfReverseMatch(originalLines.slice(matchStart), changeSegment.lines)

    if (matchEnd === matchStart) {
      // No trailing context was provided so there was no reverse match. Just match the start.
      if (changeSegment.priorExistingCode) {
        matchEnd += matchCount
      } else {
        // Since there was no context after the change, there must be prior matching lines and it must be the first few lines.
        matchEnd = countMatchingPrefixLines(originalLines, changeSegment.lines)
      }
    }
  }

  // Check if resultLines and originalLines are equal
  if (originalLines.slice(matchStart, matchEnd).join('\n') === changeSegment.lines.join('\n')) {
    return {
      status: 'no-op',
      rewrite: undefined, // No code change if lines are equal
    }
  }

  if (matchStart === 0 && changeSegment.priorExistingCode) {
    // TODO: This is a great indicator that we can fall back to rewriting the entire file.
    error_block('Failed to apply code, no starting match found', changeSegment.lines)
    return {
      status: 'error',
      rewrite: undefined,
    }
  }
  const rewrite = {
    originalLineRange: {
      start: matchStart,
      end: matchEnd,
    },
    originalLines: originalLines.slice(matchStart, matchEnd),
    newLineRange: {
      start: matchStart,
      end: matchStart + changeSegment.lines.length,
    },
    newLines: changeSegment.lines,
  }

  if (
    rewrite.originalLineRange.start === 0 &&
    rewrite.originalLineRange.end === originalLines.length &&
    originalLines.length > 100 + changeSegment.lines.length
  ) {
    info(
      'Proposed change rewrote the entire file to be much smaller. Will re-apply to whole file in case this was in error.',
    )
    return {
      status: 'error',
      rewrite,
    }
  }

  return {
    rewrite,
    status: 'success',
  }
}

function countMatchingPrefixLines(originalLines: string[], changeLines: string[]): number {
  let count = 0
  for (let i = 0; i < originalLines.length && i < changeLines.length; i++) {
    if (originalLines[i] === changeLines[i]) {
      count++
    }
  }
  return count
}

/**
 * Parses the proposed change into segments. Splits text into lines, groups
 * lines into segments based on "existing code" markers. Each segment tracks
 * if it has existing code before and after it.
 *
 * @param changeText - The text of the proposed change.
 * @returns An array of ChangeSegments.
 */
export function getChangeSegments(changeText: string): ChangeSegment[] {
  const entireChangeLines: string[] = changeText.split('\n')
  if (entireChangeLines.length === 0) {
    return []
  }

  const changeSegments: ChangeSegment[] = []
  let currentSegment: string[] = []
  const pushCurrentSegment = (postExistingCode: boolean) => {
    if (currentSegment.length > 0) {
      changeSegments.push({
        lines: currentSegment,
        priorExistingCode: changeSegments.length !== 0 || isExistingCodeLine(entireChangeLines[0]!),
        postExistingCode,
      })
      currentSegment = []
    }
  }
  for (const line of entireChangeLines) {
    if (isExistingCodeLine(line)) {
      pushCurrentSegment(true)
    } else {
      currentSegment.push(line)
    }
  }
  pushCurrentSegment(false)

  // Model sometimes includes an extra blank line before and after '... existing code ...' comments.
  for (const segment of changeSegments) {
    segment.lines = segment.lines.filter((line, index, array) => {
      if (index === 0 || index === array.length - 1) {
        return line.trim() !== ''
      }
      return true
    })
  }

  return changeSegments
}

/**
 * Finds the best forward match index between the original lines and the change lines. The best
 * match is the index where the most lines match starting from the first line of the change.
 *
 * @param originalLines - The lines of the original file.
 * @param changeLines - The lines of the proposed change.
 * @returns The index of the first line that matches the start of the change lines.
 */
export function findBestForwardMatchIndex(
  originalLines: string[],
  changeLines: string[],
): { index: number; count: number } {
  let maxMatchCount = 0
  let bestMatchIndex = 0

  for (let originalIndex = 0; originalIndex <= originalLines.length; originalIndex++) {
    let matchCount = 0
    for (
      let changeIndex = 0;
      changeIndex < changeLines.length && originalIndex + changeIndex < originalLines.length;
      changeIndex++
    ) {
      if (
        linesMatch(
          originalLines[originalIndex + changeIndex],
          changeLines[changeIndex],
          changeIndex === 0,
        )
      ) {
        matchCount++
      } else {
        break
      }
    }
    if (matchCount > maxMatchCount) {
      maxMatchCount = matchCount
      bestMatchIndex = originalIndex
    }
  }

  return { index: bestMatchIndex, count: maxMatchCount }
}

/**
 * Find the index of originalLines following the last match of changeLines. We want to find
 * the best (most overlapping) and closest (earliest in the file) match.
 *
 * Returns zero if no match because that implies the model provided no
 * closing context so we assume the last line in an insertion only.
 *
 * eg. original: [a,c,d,e,f,g] and change: [b,e,f] will return 4 because [e,f] matches.
 * For use in applyRawProposedChange this would result in outputting [b,e,f,g]
 */
export function findIndexOfReverseMatch(originalLines: string[], changeLines: string[]): number {
  let maxMatchCount = 0
  let bestMatchIndex = originalLines.length

  for (
    let originalIndex = -changeLines.length;
    originalIndex <= originalLines.length;
    originalIndex++
  ) {
    let matchCount = 0
    for (
      let changeIndex = changeLines.length - 1;
      changeIndex >= 0 && originalIndex + changeIndex >= 0;
      changeIndex--
    ) {
      if (linesMatch(originalLines[originalIndex + changeIndex], changeLines[changeIndex], false)) {
        matchCount++
      } else {
        break
      }
    }
    if (matchCount > maxMatchCount) {
      maxMatchCount = matchCount
      bestMatchIndex = originalIndex + changeLines.length - 1
    }
  }

  if (maxMatchCount === 0) {
    return 0
  }
  return bestMatchIndex + 1
}

// Hack to account for the model occasionally adding extra indentation because we show a diff which
// includes "+ " and "- " prefixes for added and removed lines.
function linesMatch(
  originalLine: string | undefined,
  changeLine: string | undefined,
  firstLine: boolean,
): boolean {
  return (
    originalLine === changeLine ||
    (firstLine && !!originalLine && !!changeLine && originalLine.endsWith(changeLine)) ||
    (!!changeLine && changeLine.startsWith('  ') && originalLine === changeLine.slice(2))
  )
}

function isExistingCodeLine(line: string): boolean {
  return line.match(/\.\.\..*existing.*\.\.\./) !== null
}

export {
  Rewrite, // Unprocessed model output.
}
